import { supabase } from '@/integrations/supabase/client';
import {
  isPGRST200Error,
  isMissingColumnError,
  normalizePaidFields,
  fetchCategoriesMap,
  fetchCostCentersMap,
  fetchClientsMap,
  fetchSuppliersMap,
} from '@/lib/supabaseResilient';
import type {
  ReportConfig,
  ReportDataPoint,
  ReportSummary,
  ReportQueryResult,
  ComparisonResult,
  DateRange,
  ReportFilters,
  DrillDownContext,
} from '@/types/custom-reports';
import type { Transaction, TransactionStatus } from '@/types';

// ============================================================================
// AUTH HELPER
// ============================================================================

/**
 * Get the authenticated user's ID
 * @throws Error if user is not authenticated
 */
async function getUserId(): Promise<string> {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) {
    throw new Error('User not authenticated');
  }
  return user.id;
}

// ============================================================================
// RESILIENT HELPERS (monolab PGRST200 + is_paid->paid)
// ============================================================================

function getEmbeddedSelect(): string {
  return `
      id,
      date,
      description,
      amount,
      type,
      is_paid,
      competence_date,
      due_date,
      categories(id, name, color),
      cost_centers(id, name, code),
      clients(id, name),
      suppliers(id, name)
    `;
}

function getPlainSelect(): string {
  return '*';
}

async function enrichRowsPlain(
  rows: any[],
  userId: string
): Promise<any[]> {
  const normalized = normalizePaidFields(rows as any[]);
  const catIds = normalized.map((r: any) => r.category_id).filter((v: any) => v != null);
  const ccIds = normalized.map((r: any) => r.cost_center_id).filter((v: any) => v != null);
  const clientIds: string[] = normalized.map((r: any) => r.client_id).filter(Boolean);
  const supplierIds: string[] = normalized.map((r: any) => r.supplier_id).filter(Boolean);
  const [catsMap, ccsMap, clientsMap, suppliersMap] = await Promise.all([
    catIds.length ? fetchCategoriesMap(catIds, userId) : Promise.resolve(new Map()),
    ccIds.length ? fetchCostCentersMap(ccIds, userId) : Promise.resolve(new Map()),
    clientIds.length ? fetchClientsMap(clientIds, userId) : Promise.resolve(new Map()),
    supplierIds.length ? fetchSuppliersMap(supplierIds, userId) : Promise.resolve(new Map()),
  ]);
  return normalized.map((row: any) => ({
    ...row,
    categories: row.category_id ? (catsMap.get(Number(row.category_id)) ?? catsMap.get(row.category_id) ?? null) : null,
    cost_centers: row.cost_center_id ? (ccsMap.get(Number(row.cost_center_id)) ?? ccsMap.get(row.cost_center_id) ?? null) : null,
    clients: row.client_id ? (clientsMap.get(row.client_id) ?? null) : null,
    suppliers: row.supplier_id ? (suppliersMap.get(row.supplier_id) ?? null) : null,
  }));
}

// ============================================================================
// QUERY CACHE
// ============================================================================

interface CacheEntry {
  data: ReportQueryResult;
  timestamp: number;
  expiresAt: number;
}

const queryCache = new Map<string, CacheEntry>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a hash key for caching based on config
 */
function hashConfig(config: ReportConfig): string {
  return JSON.stringify(config);
}

/**
 * Get cached result if available and not expired
 */
function getCachedResult(config: ReportConfig): ReportQueryResult | null {
  const key = hashConfig(config);
  const entry = queryCache.get(key);

  if (entry && Date.now() < entry.expiresAt) {
    return entry.data;
  }

  // Remove expired entries
  if (entry) {
    queryCache.delete(key);
  }

  return null;
}

/**
 * Cache a query result
 */
function setCachedResult(config: ReportConfig, data: ReportQueryResult): void {
  const key = hashConfig(config);
  const now = Date.now();

  queryCache.set(key, {
    data,
    timestamp: now,
    expiresAt: now + CACHE_TTL,
  });

  // Clean up old entries periodically
  if (queryCache.size > 100) {
    for (const [cacheKey, entry] of queryCache.entries()) {
      if (Date.now() > entry.expiresAt) {
        queryCache.delete(cacheKey);
      }
    }
  }
}

/**
 * Clear cache (call after data mutations)
 */
export function clearReportCache(): void {
  queryCache.clear();
}

// ============================================================================
// DATE RANGE UTILITIES
// ============================================================================

/**
 * Get actual start and end dates based on date range type
 */
export function getDateRangeBounds(dateRange: DateRange): { start: string; end: string } {
  const now = new Date();
  let start: Date;
  let end: Date = new Date(now.setHours(23, 59, 59, 999));

  switch (dateRange.type) {
    case 'today':
      start = new Date(now.setHours(0, 0, 0, 0));
      break;

    case 'this_week':
      start = new Date(now);
      start.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
      start.setHours(0, 0, 0, 0);
      break;

    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;

    case 'last_month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;

    case 'custom':
      start = new Date(dateRange.start!);
      end = new Date(dateRange.end!);
      end.setHours(23, 59, 59, 999);
      break;

    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

/**
 * Get previous period bounds for comparison
 */
export function getPreviousPeriodBounds(dateRange: DateRange): { start: string; end: string } {
  const current = getDateRangeBounds(dateRange);

  if (dateRange.type === 'this_month') {
    // Previous month
    const start = new Date(current.start);
    start.setMonth(start.getMonth() - 1);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  }

  if (dateRange.type === 'last_month') {
    // Two months ago
    const start = new Date(current.start);
    start.setMonth(start.getMonth() - 1);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  }

  // Default: subtract same duration from current
  const start = new Date(current.start);
  const end = new Date(current.end);
  const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  end.setDate(start.getDate() - 1);
  start.setDate(end.getDate() - diffDays + 1);

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

// ============================================================================
// QUERY BUILDER
// ============================================================================

/**
 * Build a Supabase query based on report configuration
 * Uses Supabase v2 immutable query builder pattern
 */
async function buildReportQuery(config: ReportConfig) {
  const { start, end } = getDateRangeBounds(config.dateRange);
  const userId = await getUserId();

  let query = supabase
    .from('transactions')
    .select(`
      id,
      date,
      description,
      amount,
      type,
      is_paid,
      competence_date,
      due_date,
      categories(id, name, color),
      cost_centers(id, name, code),
      clients(id, name),
      suppliers(id, name)
    `)
    .eq('user_id', userId)
    .gte('date', start)
    .lte('date', end);

  // Apply transaction type filter
  if (config.filters.transactionType !== 'all') {
    query = query.eq('type', config.filters.transactionType);
  }

  // Apply category filters
  if (config.filters.categoryIds && config.filters.categoryIds.length > 0) {
    query = query.in('category_id', config.filters.categoryIds.map(Number));
  }

  // Apply cost center filters
  if (config.filters.costCenterIds && config.filters.costCenterIds.length > 0) {
    query = query.in('cost_center_id', config.filters.costCenterIds.map(Number));
  }

  // Apply client filters
  if (config.filters.clientIds && config.filters.clientIds.length > 0) {
    query = query.in('client_id', config.filters.clientIds);
  }

  // Apply supplier filters
  if (config.filters.supplierIds && config.filters.supplierIds.length > 0) {
    query = query.in('supplier_id', config.filters.supplierIds);
  }

  // Apply payment account filters
  if (config.filters.paymentAccountIds && config.filters.paymentAccountIds.length > 0) {
    query = query.in('payment_account_id', config.filters.paymentAccountIds.map(Number));
  }

  return query;
}

/**
 * Build drill-down query with additional group filter
 */
export async function buildDrillDownQuery(context: DrillDownContext) {
  const config: ReportConfig = {
    dataSources: ['transactions'],
    dateRange: context.dateRange,
    filters: context.filters,
    groupBy: context.groupBy,
    compareWith: { enabled: false },
    metrics: { amount: 'sum' },
    visualizationType: 'table',
  };

  const { start, end } = getDateRangeBounds(context.dateRange);
  const userId = await getUserId();

  let query = supabase
    .from('transactions')
    .select(`
      id,
      date,
      description,
      amount,
      type,
      is_paid,
      competence_date,
      due_date,
      categories(id, name, color),
      cost_centers(id, name, code),
      clients(id, name),
      suppliers(id, name)
    `)
    .eq('user_id', userId)
    .gte('date', start)
    .lte('date', end);

  // Apply transaction type filter
  if (context.filters.transactionType !== 'all') {
    query = query.eq('type', context.filters.transactionType);
  }

  // Apply group-specific filter
  if (context.groupBy === 'category') {
    query = query.eq('categories.name', context.groupLabel);
  } else if (context.groupBy === 'cost_center') {
    query = query.eq('cost_centers.name', context.groupLabel);
  } else if (context.groupBy === 'client') {
    query = query.eq('clients.name', context.groupLabel);
  } else if (context.groupBy === 'supplier') {
    query = query.eq('suppliers.name', context.groupLabel);
  }

  return query;
}

// ============================================================================
// AGGREGATION FUNCTIONS
// ============================================================================

/**
 * Apply metric aggregation (sum, avg, count, min, max)
 */
function applyMetric(values: number[], metric: 'sum' | 'avg' | 'count' | 'min' | 'max'): number {
  switch (metric) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg':
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    case 'count':
      return values.length;
    case 'min':
      return values.length > 0 ? Math.min(...values) : 0;
    case 'max':
      return values.length > 0 ? Math.max(...values) : 0;
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

// ========================================================================
// STATUS NORMALIZATION
// ========================================================================

function normalizeStatusValue(status?: string | null): TransactionStatus | null {
  if (!status) return null;
  const normalized = status.toString().trim().toUpperCase();
  switch (normalized) {
    case 'PAID':
    case 'PENDING':
    case 'OVERDUE':
    case 'CANCELLED':
      return normalized as TransactionStatus;
    default:
      return null;
  }
}

function deriveStatusFromTransaction(transaction: Transaction): TransactionStatus {
  const normalized = normalizeStatusValue((transaction as any).status);
  if (normalized) {
    return normalized;
  }

  if (transaction.is_paid) {
    return 'PAID';
  }

  if (transaction.due_date) {
    const dueDate = new Date(transaction.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (Number.isFinite(dueDate.getTime()) && dueDate < today) {
      return 'OVERDUE';
    }
  }

  return 'PENDING';
}

function applyStatusFilter(
  transactions: Transaction[],
  statusFilter?: string | null
): Transaction[] {
  if (!statusFilter || statusFilter === 'all') {
    return transactions;
  }

  const target = statusFilter.toUpperCase();
  return transactions.filter((transaction) => transaction.status?.toUpperCase() === target);
}

// ============================================================================
// GROUPING FUNCTIONS
// ============================================================================

/**
 * Group transactions by the specified dimension
 */
function groupTransactions(
  transactions: Transaction[],
  groupBy: string | null
): Map<string, Transaction[]> {
  const groups = new Map<string, Transaction[]>();

  if (!groupBy) {
    groups.set('all', transactions);
    return groups;
  }

  for (const tx of transactions) {
    let key = 'Uncategorized';

    switch (groupBy) {
      case 'category':
        key = tx.categories?.name || 'Sem Categoria';
        break;
      case 'cost_center':
        key = tx.cost_centers?.name || 'Sem Centro de Custo';
        break;
      case 'client':
        key = (tx as any).clients?.name || 'Sem Cliente';
        break;
      case 'supplier':
        key = (tx as any).suppliers?.name || 'Sem Fornecedor';
        break;
      case 'month':
        key = new Date(tx.date).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' });
        break;
      case 'week':
        const date = new Date(tx.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = `Semana ${weekStart.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
        break;
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(tx);
  }

  return groups;
}

/**
 * Process grouped transactions into data points
 */
function processGroupedData(
  groups: Map<string, Transaction[]>,
  metric: 'sum' | 'avg' | 'count' | 'min' | 'max'
): ReportDataPoint[] {
  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
  ];

  let colorIndex = 0;
  const dataPoints: ReportDataPoint[] = [];

  // Sort by value descending
  const sortedGroups = Array.from(groups.entries()).sort((a, b) => {
    const aValue = applyMetric(a[1].map(tx => Math.abs(tx.amount)), metric);
    const bValue = applyMetric(b[1].map(tx => Math.abs(tx.amount)), metric);
    return bValue - aValue;
  });

  for (const [label, transactions] of sortedGroups) {
    const amounts = transactions.map(tx => tx.amount);
    const value = applyMetric(amounts, metric);

    // Get color for this group
    let color = colors[colorIndex % colors.length];

    // Try to get actual color from category if grouping by category
    if (transactions[0]?.categories?.color) {
      color = transactions[0].categories.color;
    }

    dataPoints.push({
      label,
      value,
      count: transactions.length,
      color,
    });

    colorIndex++;
  }

  return dataPoints;
}

// ============================================================================
// COMPARISON FUNCTIONS
// ============================================================================

/**
 * Calculate variance between current and previous period
 */
function calculateVariance(current: number, previous: number): {
  variance: number;
  variancePercent: number;
} {
  if (previous === 0) {
    return {
      variance: current,
      variancePercent: current > 0 ? 100 : 0,
    };
  }

  const variance = current - previous;
  const variancePercent = (variance / Math.abs(previous)) * 100;

  return { variance, variancePercent };
}

/**
 * Generate comparison result between current and previous period
 */
async function generateComparison(
  config: ReportConfig,
  currentData: ReportDataPoint[]
): Promise<ComparisonResult | undefined> {
  if (!config.compareWith.enabled) {
    return undefined;
  }

  // Get previous period bounds
  const previousBounds = getPreviousPeriodBounds(config.dateRange);

  // Create config for previous period
  const previousConfig: ReportConfig = {
    ...config,
    dateRange: {
      type: 'custom',
      start: previousBounds.start,
      end: previousBounds.end,
    },
  };

  // Execute query for previous period
  const previousResult = await executeReportQuery(previousConfig);

  // Calculate totals
  const currentTotal = currentData.reduce((sum, dp) => sum + Math.abs(dp.value), 0);
  const previousTotal = previousResult.data.reduce((sum, dp) => sum + Math.abs(dp.value), 0);

  const { variance, variancePercent } = calculateVariance(currentTotal, previousTotal);

  return {
    current: { total: currentTotal, count: currentData.length, data: currentData },
    previous: { total: previousTotal, count: previousResult.data.length, data: previousResult.data },
    variance,
    variancePercent,
  };
}

// ============================================================================
// MAIN QUERY EXECUTION
// ============================================================================

/**
 * Execute report query and return processed results
 */
export async function executeReportQuery(config: ReportConfig): Promise<ReportQueryResult> {
  // Check cache first
  const cached = getCachedResult(config);
  if (cached) {
    return cached;
  }

  // Build and execute query with resilient fallback for monolab (PGRST200 + is_paid->paid)
  let transactions: any[] | null = null;
  let lastError: any = null;
  try {
    const query = await buildReportQuery(config);
    const res: any = await query;
    if (res.error) {
      if (isMissingColumnError(res.error, 'is_paid')) {
        // is_paid column missing — retry using paid (rebuild query with paid)
        console.warn('[reportQueryBuilder] is_paid missing, retrying with plain select + manual enrich');
        const userId = await getUserId();
        const { start, end } = getDateRangeBounds(config.dateRange);
        let q2: any = supabase.from('transactions').select(getPlainSelect()).eq('user_id', userId).gte('date', start).lte('date', end);
        if (config.filters.transactionType !== 'all') q2 = q2.eq('type', config.filters.transactionType);
        if (config.filters.categoryIds?.length) q2 = q2.in('category_id', config.filters.categoryIds.map(Number));
        if (config.filters.costCenterIds?.length) q2 = q2.in('cost_center_id', config.filters.costCenterIds.map(Number));
        if (config.filters.clientIds?.length) q2 = q2.in('client_id', config.filters.clientIds);
        if (config.filters.supplierIds?.length) q2 = q2.in('supplier_id', config.filters.supplierIds);
        if (config.filters.paymentAccountIds?.length) q2 = q2.in('payment_account_id', config.filters.paymentAccountIds.map(Number));
        const r2: any = await q2;
        if (r2.error) throw r2.error;
        transactions = await enrichRowsPlain(r2.data ?? [], userId);
      } else if (isPGRST200Error(res.error)) {
        console.warn('[reportQueryBuilder] PGRST200 embedded, fallback plain + manual enrich', res.error.message);
        const userId = await getUserId();
        const { start, end } = getDateRangeBounds(config.dateRange);
        let q2: any = supabase.from('transactions').select(getPlainSelect()).eq('user_id', userId).gte('date', start).lte('date', end);
        if (config.filters.transactionType !== 'all') q2 = q2.eq('type', config.filters.transactionType);
        if (config.filters.categoryIds?.length) q2 = q2.in('category_id', config.filters.categoryIds.map(Number));
        if (config.filters.costCenterIds?.length) q2 = q2.in('cost_center_id', config.filters.costCenterIds.map(Number));
        if (config.filters.clientIds?.length) q2 = q2.in('client_id', config.filters.clientIds);
        if (config.filters.supplierIds?.length) q2 = q2.in('supplier_id', config.filters.supplierIds);
        if (config.filters.paymentAccountIds?.length) q2 = q2.in('payment_account_id', config.filters.paymentAccountIds.map(Number));
        const r2: any = await q2;
        if (r2.error) throw r2.error;
        transactions = await enrichRowsPlain(r2.data ?? [], userId);
      } else {
        throw res.error;
      }
    } else {
      transactions = normalizePaidFields((res.data ?? []) as any[]);
    }
  } catch (e: any) {
    lastError = e;
    throw lastError;
  }
  if (!transactions) transactions = [];

  const normalizedTransactions = (transactions || []).map((transaction) => ({
    ...transaction,
    status: deriveStatusFromTransaction(transaction),
  }));

  const filteredTransactions = applyStatusFilter(
    normalizedTransactions,
    config.filters.status
  );

  // Group transactions
  const groups = groupTransactions(filteredTransactions, config.groupBy);

  // Process grouped data
  const data = processGroupedData(groups, config.metrics.amount);

  // Calculate summary
  const allAmounts = filteredTransactions.map(tx => tx.amount);
  const summary: ReportSummary = {
    total: applyMetric(allAmounts, 'sum'),
    average: applyMetric(allAmounts, 'avg'),
    min: applyMetric(allAmounts, 'min'),
    max: applyMetric(allAmounts, 'max'),
    count: allAmounts.length,
  };

  // Generate comparison if enabled
  const comparison = config.compareWith.enabled
    ? await generateComparison(config, data)
    : undefined;

  // Build result
  const result: ReportQueryResult = {
    data,
    summary,
    comparison,
    metadata: {
      generatedAt: new Date().toISOString(),
      dataSource: config.dataSources,
      filters: config.filters,
      dateRange: config.dateRange,
    },
  };

  // Cache result
  setCachedResult(config, result);

  return result;
}

/**
 * Execute drill-down query
 */
export async function executeDrillDownQuery(context: DrillDownContext): Promise<Transaction[]> {
  let data: any[] | null = null;
  let error: any = null;
  try {
    const query = await buildDrillDownQuery(context);
    const res: any = await query;
    data = res.data as any;
    error = res.error as any;
    if (error) {
      if (isPGRST200Error(error) || isMissingColumnError(error, 'is_paid')) {
        console.warn('[reportQueryBuilder] drillDown fallback (PGRST200/is_paid)', error.message);
        const userId = await getUserId();
        const { start, end } = getDateRangeBounds(context.dateRange);
        let q2: any = supabase.from('transactions').select(getPlainSelect()).eq('user_id', userId).gte('date', start).lte('date', end);
        if (context.filters.transactionType !== 'all') q2 = q2.eq('type', context.filters.transactionType);
        // cannot filter by embedded name in plain mode; fetch all then filter in JS after enrich
        const r2: any = await q2;
        if (r2.error) throw r2.error;
        const enriched = await enrichRowsPlain(r2.data ?? [], userId);
        // apply group-specific filter in JS
        let filtered = enriched;
        if (context.groupBy === 'category') filtered = enriched.filter((r: any) => (r.categories?.name ?? '') === context.groupLabel);
        else if (context.groupBy === 'cost_center') filtered = enriched.filter((r: any) => (r.cost_centers?.name ?? '') === context.groupLabel);
        else if (context.groupBy === 'client') filtered = enriched.filter((r: any) => (r.clients?.name ?? '') === context.groupLabel);
        else if (context.groupBy === 'supplier') filtered = enriched.filter((r: any) => (r.suppliers?.name ?? '') === context.groupLabel);
        data = filtered;
        error = null;
      } else {
        throw error;
      }
    } else {
      data = normalizePaidFields((data ?? []) as any[]);
    }
  } catch (e: any) {
    throw e;
  }
  if (error) throw error;

  const normalizedTransactions = (data || []).map((transaction) => ({
    ...transaction,
    status: deriveStatusFromTransaction(transaction),
  }));

  const filteredTransactions = applyStatusFilter(
    normalizedTransactions,
    context.filters.status
  );

  return filteredTransactions;
}
